import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useCallback, useEffect } from 'react';
import Cookie from 'js-cookie';
import { AccordionSummary, AccordionDetails, Accordion } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';

import { BodySmallText, ButtonText, Subhead02Text } from '../typography/typography';
import ModalContainer from '../common/modal-container/modal-container';
import ControlledSwitch from '../common/forms/ControlledSwitch';
import { hideCookies, setPolicyRoute, setShowCookies } from '../../store/slices/generalSlice';
import { RootState } from '../../store/rootReducer';
import { ManageCookiesStyle } from './manage-cookies-style';

export type Cookies = {
  accepted: boolean;
  options: {
    mandatory: true;
    optional: boolean;
  };
};

const defaultCookies: Cookies = {
  accepted: true,
  options: {
    mandatory: true,
    optional: true,
  },
};

const ManageCookies = () => {
  const { t } = useTranslation(['common']);

  const dispatch = useDispatch();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { showCookies, policyRoute } = useSelector((state: RootState) => state.general);

  const { reset, handleSubmit, control } = useForm<{ optional: boolean }>({
    defaultValues: { optional: true },
    reValidateMode: 'onChange',
  });

  const acceptAllCokies = () => {
    Cookie.set('cookie-config', JSON.stringify(defaultCookies), { expires: 7 });
    dispatch(hideCookies());
    // TODO: 👇 temporary fix, change later
    navigator.geolocation.getCurrentPosition(
      () => {},
      () => {},
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 },
    );
  };

  const initCookies = useCallback(() => {
    let show = false;
    const cookieConfig = Cookie.get('cookie-config');
    if (!cookieConfig) {
      show = true;
    } else {
      const cookiesParsed: Cookies | undefined = JSON.parse(cookieConfig);
      if (!cookiesParsed?.accepted) show = true;
      reset({ optional: !!cookiesParsed?.options?.optional });
    }
    if (show) dispatch(setShowCookies({ cookies: 'first' }));
  }, [dispatch, reset]);

  const onAcceptCookies = (data: { optional: boolean }) => {
    const newCookies: Cookies = {
      accepted: true,
      options: {
        mandatory: true,
        optional: data.optional,
      },
    };
    Cookie.set('cookie-config', JSON.stringify(newCookies), { expires: 7 });
    dispatch(hideCookies());
  };

  const onCancelSecondModal = () => {
    const cookieConfig = Cookie.get('cookie-config');
    if (cookieConfig) dispatch(hideCookies());
    else dispatch(setShowCookies({ cookies: 'first' }));
  };

  useEffect(() => {
    initCookies();
  }, [initCookies]);

  return (
    <ManageCookiesStyle>
      <ModalContainer
        buttons={[
          {
            className: 'primary',
            onClick: () => acceptAllCokies(),
            label: t('common:modal-cookies.accept'),
            dataCy: 'accept-all-cookies',
          },
          {
            className: 'secondary',
            onClick: () => dispatch(setShowCookies({ cookies: 'second' })),
            label: t('common:modal-cookies.manage'),
            dataCy: 'manage-cookies',
          },
        ]}
        required
        active={showCookies === 'first'}
        onClose={() => ''}
        title={t('common:modal-cookies.title')}
        text={
          <Trans t={t} i18nKey="common:modal-cookies.text">
            <span
              onClick={() => {
                dispatch(setPolicyRoute({ policy: 'cookies' }));
              }}
              className="cookies-link"
            >
              Aquí
            </span>
          </Trans>
        }
        dataCy="reset-form-modal"
      />
      <ModalContainer
        fullScreen
        showLeftButton={false}
        className={'buttons-relative'}
        buttons={[
          {
            className: 'primary',
            onClick: handleSubmit(onAcceptCookies),
            label: t('common:modal-cookies.accept'),
          },
          {
            className: 'secondary',
            onClick: onCancelSecondModal,
            label: t('common:modal-cookies.cancel'),
          },
        ]}
        clickLeft={() => ''}
        active={showCookies === 'second'}
        onClose={onCancelSecondModal}
        title={t('common:manage-cookies.title')}
        text={
          <Trans t={t} i18nKey="common:manage-cookies.text">
            De conformidad con el artículo 22 de la Ley de Servicios de la Sociedad de la Información y Comercio
            Electrónico así como en el Considerando (30) RGPD, la presente Página Web recaba cookies tanto propias como
            de terceros con diversas finalidades, siendo la principal la personalización de la experiencia de nuestros
            usuarios. Una cookie es un fichero que se descarga en el ordenador / smartphone / tablet del usuario cuando
            éste accede a determinadas páginas web con la finalidad de almacenar y recuperar información sobre la
            navegación que se efectúa desde dicho equipo. Para garantizar el derecho a la privacidad de nuestros
            usuarios, se ofrece la opción de activar y desactivar los distintos tipos de cookies que usa nuestro sitio.
            Rechazar o deshabilitar el uso de cookies puede tener un impacto en la experiencia en nuestro sitio web, sin
            embargo podrá seguir accediendo y utilizando nuestro servicio con normalidad. Para conocer más información
            sobre las cookies, le invitamos a acceder a nuestra {''}
            <span
              onClick={() => {
                dispatch(setPolicyRoute({ policy: 'cookies' }));
              }}
              className="cookies-link"
            >
              Política de cookies
            </span>
            . Más información sobre nuestra Política de Privacidad
            <span
              className="cookies-link"
              onClick={() => {
                dispatch(setPolicyRoute({ policy: 'privacy' }));
              }}
            >
              aquí
            </span>
            .
          </Trans>
        }
        // dataCy="reset-form-modal"
      >
        <div className="cookie-allow">
          <Subhead02Text weight={600}>{t('common:manage-cookies.allow')}</Subhead02Text>
        </div>
        <div className="cookie-item">
          <div className="cookie-item__accordion">
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="panel1a-header">
                <BodySmallText weight={600}>{t('common:manage-cookies.mandatory-cookies.title')}</BodySmallText>
              </AccordionSummary>
              <AccordionDetails>
                <BodySmallText>{t('common:manage-cookies.mandatory-cookies.description')}</BodySmallText>
              </AccordionDetails>
            </Accordion>
          </div>
          <div className="cookie-item__switch">
            <button className="cookie-item-button allow">
              <ButtonText>{t('common:buttons.accept')}</ButtonText>
            </button>
            <button className="cookie-item-button ">
              <ButtonText>{t('common:buttons.reject')}</ButtonText>
            </button>
          </div>
        </div>
        <div className="cookie-item">
          <div className="cookie-item__accordion">
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="panel1a-header">
                <BodySmallText weight={600}>{t('common:manage-cookies.optional-cookies.title')}</BodySmallText>
              </AccordionSummary>
              <AccordionDetails>
                <BodySmallText>{t('common:manage-cookies.optional-cookies.description')}</BodySmallText>
              </AccordionDetails>
            </Accordion>
          </div>
          <ControlledSwitch controllerProps={{ name: 'optional', control }} />
        </div>
      </ModalContainer>
    </ManageCookiesStyle>
  );
};

export default ManageCookies;
